<script setup>
import { ref, onMounted, watch } from 'vue';
import schedulingAPI from '../api/scheduling';

const reportData = ref(null);
const isLoading = ref(false);

const period = ref('30_days');

const fetchReports = async () => {
  isLoading.value = true;
  try {
    let start_date = new Date();
    if (period.value === '7_days') {
      start_date.setDate(start_date.getDate() - 7);
    } else if (period.value === '30_days') {
      start_date.setDate(start_date.getDate() - 30);
    } else if (period.value === '90_days') {
      start_date.setDate(start_date.getDate() - 90);
    }

    const res = await schedulingAPI.getReports({
      start_date: start_date.toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
    });
    reportData.value = res.data;
  } catch (err) {
    console.error('Erro ao carregar relatórios:', err);
  } finally {
    isLoading.value = false;
  }
};

const formatPrice = val => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(val) || 0);
};

watch(period, () => {
  fetchReports();
});

onMounted(() => {
  fetchReports();
});
</script>

<template>
  <div class="space-y-5 select-none">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-base font-semibold text-n-slate-12">Relatórios & Métricas</h2>
        <p class="text-xs text-n-slate-11">
          Acompanhe o volume de atendimentos, faturamento por serviço e comparecimento.
        </p>
      </div>

      <select
        v-model="period"
        class="h-8 px-3 text-xs bg-n-surface-1 border border-n-weak rounded-lg text-n-slate-12 focus:border-brand focus:outline-none cursor-pointer transition-colors shadow-xs"
      >
        <option value="7_days">Últimos 7 dias</option>
        <option value="30_days">Últimos 30 dias</option>
        <option value="90_days">Últimos 90 dias</option>
      </select>
    </div>

    <div v-if="isLoading" class="flex justify-center items-center py-16">
      <div class="animate-spin rounded-full h-7 w-7 border-2 border-brand border-t-transparent"></div>
    </div>

    <template v-else-if="reportData">
      <!-- KPI Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        <!-- Total Appointments -->
        <div class="p-4 rounded-xl bg-n-surface-1 border border-n-weak shadow-xs">
          <span class="text-xs font-medium text-n-slate-11">Total de Agendamentos</span>
          <div class="text-2xl font-bold text-n-slate-12 mt-1">
            {{ reportData.summary.total_appointments }}
          </div>
          <span class="text-[11px] text-n-brand font-medium">
            {{ reportData.summary.confirmed }} confirmados
          </span>
        </div>

        <!-- Completed Appointments -->
        <div class="p-4 rounded-xl bg-n-surface-1 border border-n-weak shadow-xs">
          <span class="text-xs font-medium text-n-slate-11">Atendimentos Concluídos</span>
          <div class="text-2xl font-bold text-emerald-500 mt-1">
            {{ reportData.summary.completed }}
          </div>
          <span class="text-[11px] text-n-slate-10">
            {{ reportData.summary.cancelled }} cancelados
          </span>
        </div>

        <!-- Attendance Rate -->
        <div class="p-4 rounded-xl bg-n-surface-1 border border-n-weak shadow-xs">
          <span class="text-xs font-medium text-n-slate-11">Taxa de Comparecimento</span>
          <div class="text-2xl font-bold text-purple-500 mt-1">
            {{ reportData.summary.attendance_rate }}%
          </div>
          <span class="text-[11px] text-n-slate-10">
            {{ reportData.summary.no_show }} ausência(s) (no-show)
          </span>
        </div>

        <!-- Revenue -->
        <div class="p-4 rounded-xl bg-n-surface-1 border border-n-weak shadow-xs">
          <span class="text-xs font-medium text-n-slate-11">Faturamento Realizado</span>
          <div class="text-2xl font-bold text-emerald-500 mt-1 font-mono">
            {{ formatPrice(reportData.summary.completed_revenue) }}
          </div>
          <span class="text-[11px] text-n-slate-10">
            Previsto: {{ formatPrice(reportData.summary.total_revenue) }}
          </span>
        </div>
      </div>

      <!-- Breakdown by Service & by Professional -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- By Service -->
        <div class="p-4 rounded-xl bg-n-surface-1 border border-n-weak shadow-xs">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Desempenho por Serviço</h3>
          <div v-if="reportData.by_service.length === 0" class="text-xs text-n-slate-10 py-6 text-center">
            Sem dados no período.
          </div>
          <div v-else class="space-y-2">
            <div
              v-for="item in reportData.by_service"
              :key="item.service_name"
              class="flex items-center justify-between p-2.5 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak text-xs"
            >
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full shrink-0" :style="{ backgroundColor: item.color || '#3B82F6' }" />
                <span class="font-medium text-n-slate-12">{{ item.service_name }}</span>
              </div>
              <div class="text-right">
                <span class="font-medium text-n-slate-11 block">{{ item.appointments_count }} agendamento(s)</span>
                <span class="text-[11px] text-emerald-500 font-semibold font-mono">{{ formatPrice(item.revenue) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- By Professional -->
        <div class="p-4 rounded-xl bg-n-surface-1 border border-n-weak shadow-xs">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Desempenho por Profissional</h3>
          <div v-if="reportData.by_professional.length === 0" class="text-xs text-n-slate-10 py-6 text-center">
            Sem dados no período.
          </div>
          <div v-else class="space-y-2">
            <div
              v-for="item in reportData.by_professional"
              :key="item.professional_name"
              class="flex items-center justify-between p-2.5 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak text-xs"
            >
              <span class="font-medium text-n-slate-12">{{ item.professional_name }}</span>
              <div class="text-right">
                <span class="font-medium text-n-slate-11 block">{{ item.appointments_count }} atendimento(s)</span>
                <span class="text-[11px] text-emerald-500 font-semibold font-mono">{{ formatPrice(item.revenue) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
